import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import useI18n from '../../i18n/useI18n'
import Timeline from '../../components/timeline'
import Pagination from '../../components/pagination'
import AnnouncementFormModal from './AnnouncementFormModal'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import useTableDataSync from '../../infrastructure/event-bus/hooks/use-table-data-sync'
import RoleBasedAccess from '../../components/role-based-access'
import { UserTypeEnum } from '../../utils/enums'
import general from '../../utils/general'
import EventBus from '../../infrastructure/event-bus/event-bus'
import AnnouncementsFilter, { announcementFilterDefaults } from './filter'
import './index.scss'

const getTabKeyFromType = (typeValue) => {
  if (!typeValue) {
    return 'current'
  }
  return String(typeValue).toLowerCase()
}

const Announcement = () => {
  const dispatch = useDispatch()
  const { t } = useI18n()
  const [announcements, setAnnouncements] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1
  })
  const [filters, setFilters] = useState({ ...announcementFilterDefaults })
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null)
  const [activeTab, setActiveTab] = useState('current')

  const buildQueryParams = (page, filterParams = {}, limitValue = null) => {
    const appliedFilters = { ...announcementFilterDefaults, ...filterParams }
    const query = {
      page,
      limit: limitValue || pagination.limit
    }

    if (appliedFilters.search?.trim()) {
      query.search = appliedFilters.search.trim()
    }
    if (appliedFilters.type) {
      query.type = appliedFilters.type
    }
    if (appliedFilters.target_user_type) {
      query.target_user_type = appliedFilters.target_user_type
    }
    if (appliedFilters.is_published !== '' && appliedFilters.is_published !== null) {
      query.is_published = appliedFilters.is_published === 'true'
    }
    if (appliedFilters.expires_at) {
      query.expires_at = appliedFilters.expires_at
    }
    if (appliedFilters.start_date) {
      query.start_date = appliedFilters.start_date
    }
    if (appliedFilters.end_date) {
      query.end_date = appliedFilters.end_date
    }

    return query
  }

  const fetchAnnouncements = (page = 1, filterParams = filters, limitOverride = null) => {
    setLoading(true)
    const effectiveLimit = limitOverride || pagination.limit
    const appliedFilters = { ...announcementFilterDefaults, ...(filterParams || {}) }

    dispatch(
      ApiCallActions.Get({
        url: 'announcements',
        query: buildQueryParams(page, appliedFilters, effectiveLimit),
        onSuccess: ({ data }) => {
          setAnnouncements(data.data || [])
          setPagination({
            total: data.meta?.total || 0,
            page: data.meta?.page || page,
            limit: data.meta?.limit || effectiveLimit,
            totalPages: data.meta?.totalPages || 1
          })
          setLoading(false)
        },
        onError: () => {
          setAnnouncements([])
          setLoading(false)
        }
      })
    )
  }

  useEffect(() => {
    fetchAnnouncements(1, filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useTableDataSync(
    'announcement',
    announcements,
    setAnnouncements,
    pagination,
    setPagination,
    (page, filterParams) => fetchAnnouncements(page, filterParams || filters),
    filters
  )

  const handlePageChange = (page) => {
    fetchAnnouncements(page, filters)
  }

  const handleLimitChange = (newLimit) => {
    const updatedPagination = {
      ...pagination,
      limit: newLimit,
      page: 1
    }
    setPagination(updatedPagination)
    fetchAnnouncements(1, filters, newLimit)
  }

  const handleFilterChange = (newFilters) => {
    const normalizedFilters = { ...announcementFilterDefaults, ...newFilters }
    setFilters(normalizedFilters)
    setActiveTab(getTabKeyFromType(normalizedFilters.type))
  }

  const handleFilterSubmit = (newFilters) => {
    const normalizedFilters = { ...announcementFilterDefaults, ...newFilters }
    setFilters(normalizedFilters)
    setActiveTab(getTabKeyFromType(normalizedFilters.type))
    fetchAnnouncements(1, normalizedFilters)
  }

  const handleClearFilters = () => {
    const resetFilters = { ...announcementFilterDefaults }
    setFilters(resetFilters)
    setActiveTab('current')
    fetchAnnouncements(1, resetFilters)
  }

  const handleRefresh = () => {
    fetchAnnouncements(pagination.page, filters, pagination.limit)
  }

  const handleAddClick = () => {
    setSelectedAnnouncement(null)
    setIsFormModalOpen(true)
  }

  const handleEditClick = (announcement) => {
    setSelectedAnnouncement(announcement)
    setIsFormModalOpen(true)
  }

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false)
    setSelectedAnnouncement(null)
  }

  const handleFormSuccess = () => {
    fetchAnnouncements(pagination.page, filters, pagination.limit)
  }

  const handleDeleteClick = async (announcement) => {
    const title = announcement.title || t('ANNOUNCEMENTS_ENTITY') || 'Duyuru'
    const confirmed = await general.confirmDelete(t('ANNOUNCEMENTS_ENTITY') || 'Duyuru', title)

    if (!confirmed) {
      return
    }

    dispatch(
      ApiCallActions.Delete({
        url: `announcements/${announcement.id}`,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: () => {
          EventBus.sc.onEntityDeleted({
            entityType: 'announcement',
            entityId: announcement.id
          })
          general.showDeleteSuccess(t('ANNOUNCEMENTS_ENTITY') || 'Duyuru')
        }
      })
    )
  }

  // Timeline için duyuruları hazırla (action fonksiyonları ile)
  const timelineItems = announcements.map((announcement) => ({
    ...announcement,
    onEdit: handleEditClick,
    onDelete: handleDeleteClick
  }))

  const filterByTab = (items, tabKey) => {
    if (tabKey === 'current') return items
    return items.filter((item) => {
      const itemType = (item.type || item.announcementType || '').toLowerCase()
      return itemType === tabKey
    })
  }

  const tabs = [
    { key: 'current', label: 'Güncel', icon: 'zmdi-time' },
    { key: 'general', label: 'Genel', icon: 'zmdi-globe' },
    { key: 'agent', label: 'Acente', icon: 'zmdi-badge-check' },
    { key: 'student', label: 'Öğrenci', icon: 'zmdi-graduation-cap' }
  ]

  const filteredTimelineItems = filterByTab(timelineItems, activeTab)

  const getTabButtonClass = (tabKey) =>
    `announcement-tab-btn ${activeTab === tabKey ? 'is-active' : 'is-inactive'}`

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey)
    const updatedFilters = {
      ...filters,
      type: tabKey === 'current' ? '' : tabKey
    }
    setFilters(updatedFilters)
    fetchAnnouncements(1, updatedFilters)
  }

  return (
    <section className="content">
      <div className="block-header">
        <div className="row">
          <div className="col-lg-7 col-md-6 col-sm-12 text-left">
            <h2>
              {t('ANNOUNCEMENTS_LIST_TITLE') || 'Duyurular'}
              <small>{t('ANNOUNCEMENTS_LIST_SUBTITLE') || 'Tüm duyuruları buradan görüntüleyebilirsiniz'}</small>
            </h2>
          </div>
          <div className="col-lg-5 col-md-6 col-sm-12">
            <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
              <button
                className="btn btn-white btn-icon btn-round float-right m-l-10"
                onClick={handleAddClick}
                title={t('ANNOUNCEMENTS_ADD_BUTTON') || 'Yeni Duyuru Ekle'}
              >
                <i className="zmdi zmdi-plus"></i>
               </button>
            </RoleBasedAccess>
            <ul className="breadcrumb float-md-right">
              <li className="breadcrumb-item">
                <a href="/dashboard">
                  <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i> {t('COMMON_HOME')}
                </a>
              </li>
              <li className="breadcrumb-item active">{t('ANNOUNCEMENTS_LIST_TITLE') || 'Duyurular'}</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid">
        <div className="row clearfix">
          <div className="col-lg-12">
            <AnnouncementsFilter
              filters={filters}
              onFilterChange={handleFilterChange}
              onFilterSubmit={handleFilterSubmit}
              onClearFilters={handleClearFilters}
              onRefresh={handleRefresh}
              loading={loading}
            />
          </div>
          <div className="col-lg-12">
            <div className="card">
              <div className="body">
                {/* <div className="announcement-tabs-wrapper d-flex flex-wrap align-items-center m-b-20">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={getTabButtonClass(tab.key)}
                      onClick={() => handleTabChange(tab.key)}
                    >
                      {tab.icon && <i className={`zmdi ${tab.icon}`}></i>}
                      {tab.label}
                    </button>
                  ))}
                </div> */}

                <Timeline
                  items={filteredTimelineItems}
                  loading={loading}
                  emptyMessage={t('ANNOUNCEMENTS_EMPTY_MESSAGE') || 'Henüz duyuru bulunmamaktadır.'}
                />
              </div>
            </div>
          </div>
          <div className="col-lg-12">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          </div>
        </div>
      </div>

      <AnnouncementFormModal
        isOpen={isFormModalOpen}
        announcement={selectedAnnouncement}
        onClose={handleCloseFormModal}
        onSuccess={handleFormSuccess}
      />
    </section>
  )
}

export default Announcement